import React from 'react';
import { useTranslation } from 'react-i18next';
import { Code2, KeyRound, Star, MousePointerClick, BarChart3, Building2 } from 'lucide-react';

/**
 * Short, self-serve documentation for the embeddable widgets — rendered as the
 * third tab of the Widget Management hub.
 */
const WidgetDocs: React.FC = () => {
  const { t } = useTranslation();

  const sections = [
    {
      icon: Code2,
      title: t('reviewsWidget.docs.embedTitle', 'Embedding the widget'),
      body: t(
        'reviewsWidget.docs.embedBody',
        'Copy the embed snippet from the Public Reviews Widget tab and paste it into your website’s HTML where you want the reviews to appear. The included <div id="clareefai-reviews-widget"></div> marks the spot; the script renders inside it. No framework or build step is required — it works on any site.',
      ),
    },
    {
      icon: KeyRound,
      title: t('reviewsWidget.docs.tokenTitle', 'Your widget token'),
      body: t(
        'reviewsWidget.docs.tokenBody',
        'The token (rvw_pub_…) identifies your account to the widget. It is a public identifier — safe to expose in page source — and only grants read access to reviews you have published. Regenerate it any time to invalidate existing embeds (you’ll then need to update the snippet on your site).',
      ),
    },
    {
      icon: Star,
      title: t('reviewsWidget.docs.contentTitle', 'Which reviews appear'),
      body: t(
        'reviewsWidget.docs.contentBody',
        'Only reviews you have published (or featured) are ever shown. Choose Automatic to pull them by rule (minimum rating, count, order), or Hand-picked to select specific reviews. Changes take effect after you click Save.',
      ),
    },
    {
      icon: MousePointerClick,
      title: t('reviewsWidget.docs.ctaTitle', 'The call-to-action'),
      body: t(
        'reviewsWidget.docs.ctaBody',
        'When you set a CTA label, visitors who click it get a short contact form. Submissions land in your Demo Requests, exactly like the “Book a Demo” button on your public page — so your team follows up in one place.',
      ),
    },
    {
      icon: BarChart3,
      title: t('reviewsWidget.docs.analyticsTitle', 'Understanding the analytics'),
      body: t(
        'reviewsWidget.docs.analyticsBody',
        'Widget Views = times the widget loaded on a page. CTA Clicks = times the form was opened. Calls Requested = forms submitted. Conversion Rate = Calls Requested ÷ Views.',
      ),
    },
    {
      icon: Building2,
      title: t('reviewsWidget.docs.backofficeTitle', 'Backoffice widget'),
      body: t(
        'reviewsWidget.docs.backofficeBody',
        'The Backoffice Widget is a separate, lead-capture button you embed in your own app so your users can sign up as promoters. Configure it on the Backoffice Widget tab.',
      ),
    },
  ];

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6 max-w-3xl">
      <h2 className="text-lg font-bold text-gray-900 mb-1">{t('reviewsWidget.docs.title', 'How the widgets work')}</h2>
      <p className="text-sm text-gray-600 mb-6">
        {t('reviewsWidget.docs.subtitle', 'A quick guide to embedding and configuring your widgets.')}
      </p>
      <div className="space-y-6">
        {sections.map((s, i) => (
          <div key={i} className="flex gap-4">
            <div className="w-9 h-9 rounded-lg bg-purple-100 flex items-center justify-center flex-shrink-0">
              <s.icon className="w-4.5 h-4.5 text-purple-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 text-sm mb-1">{s.title}</h3>
              <p className="text-sm text-gray-600 leading-relaxed">{s.body}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default WidgetDocs;
